import { Autoplay, Navigation, Pagination } from 'swiper';
import { Box, Button, Typography, alpha, makeStyles, useMediaQuery } from '@material-ui/core';
import { Swiper, SwiperSlide } from 'swiper/react';
import { useEffect, useState } from 'react';

import { IOffers } from 'interface/offers';
import fallbackBannerLarge from 'assets/img/showcase-banner-large.png';
import fallbackBannerMedium from 'assets/img/showcase-banner-medium.png';
import fallbackBannerSmall from 'assets/img/showcase-banner-small.png';
import { RENEWAL_FEATURE } from 'settings';
import RenewalModal from 'components/Shared/RenewalModal';
import { fetchBannerOffers } from 'services/offer';
import { useFlag } from 'hooks/useFlag';

interface IBannerSlider {
  courseHash: string;
  bannerDefault: string;
}

const useStyles = makeStyles((theme) => ({
  slider: {
    marginTop: '40px',
    '& .swiper-button-next, & .swiper-button-prev': {
      height: 500,
      width: 50,
      top: 0,
      right: 0,
      cursor: 'pointer',
      display: 'flex',
      alingItens: 'center',
      [theme.breakpoints.down(780)]: {
        display: 'none',
      },
    },
    '& .swiper-button-next': {
      background: alpha(theme.palette.neutrals.black, 0.7),
    },

    '&:hover': {
      '& #upsell-showcase-banner-info': {
        transform: 'translateX(0px)',
      },
    },
    '& .swiper-pagination-bullet': {
      backgroundColor: theme.palette.common.white,
    },
  },
  sliderItem: {
    width: 'max-content',
    overflow: 'hidden',
  },
  image: {
    width: '100%',
    minWidth: '100%',
  },
  banner: {
    position: 'absolute',
    display: 'flex',
    background: alpha(theme.palette.neutrals.black, 0.8),
    width: '500px',
    height: '100%',
    transition: 'all 0.3s ease',
    alignItems: 'center',
    transform: 'translateX(-500px)',
    '& h3': {
      fontSize: '20px',
      color: theme.palette.common.white,
    },
    '&::after': {
      position: 'absolute',
      content: "''",
      top: 0,
      right: -250,
      width: 250,
      height: '100%',
      background: alpha(theme.palette.neutrals.black, 0.8),
      clipPath: 'polygon(0 0, 0% 0%, 100% 0%, 0 100%)',
    },
  },
  imageBanner: {
    maxHeight: 460,
    width: '100%',
  },
  bannerContent: {
    marginLeft: '40px',
    position: 'relative',
    overflow: 'hidden',
  },
  wrapperTitle: {
    width: '100%',
    maxHeight: '120px',
    overflow: 'hidden',
    wordBreak: 'break-word',
  },
  bannerImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  previewContainer: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    flexDirection: 'column',
    alignItems: 'center',
  },
  title: {
    fontWeight: 700,
    color: theme.palette.common.white,
  },
  price: {
    fontWeight: 600,
    color: theme.palette.primary.main,
  },
  validity: {
    color: theme.palette.primary.contrastText,
  },
  content: {
    width: '80%',
    minWidth: '240px',
    height: 'max-content',
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '100px',
    [theme.breakpoints.down(1023)]: {
      marginLeft: '0px',
    },
  },
  contentInBody: {
    marginBottom: '20px',
    marginTop: '20px',
    [theme.breakpoints.down(1023)]: {
      width: '100%',
      marginLeft: '13vw',
      marginRight: '13vw',
    },
    [theme.breakpoints.down(768)]: {
      width: '100%',
      marginLeft: '8vw',
      marginRight: '8vw',
    },
  },
  renewalButton: {
    width: 'fit-content',
  },
}));

export const OfferBanner = ({ courseHash, bannerDefault }: IBannerSlider) => {
  const [listOffers, setListOffers] = useState<IOffers>();
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [openRenewalModal, setOpenRenewalModal] = useState(false);

  const classes = useStyles();
  const isScreenLarge = useMediaQuery('(min-width: 1024px)');
  const isScreenMedium = useMediaQuery('(min-width: 481px) and (max-width: 1023px)');
  const isScreenSmall = useMediaQuery('(max-width: 480px)');
  const hasFlag = useFlag(RENEWAL_FEATURE);

  const handleClickOpenRenewalModal = () => {
    setOpenRenewalModal(true);
  };

  const handleCloseRenewalModal = () => {
    setOpenRenewalModal(false);
  };

  const previewImage = (bannerImage: any) => {
    if (isScreenMedium) return bannerImage.medium ?? fallbackBannerMedium;

    if (isScreenSmall) return bannerImage.small ?? fallbackBannerSmall;

    return bannerImage.large ?? fallbackBannerLarge;
  };

  useEffect(() => {
    if (hasFlag) {
      fetchBannerOffers(courseHash).then((response) => {
        setListOffers(response && response[0]);
      });
    }
  }, [hasFlag, courseHash]);

  const content = (renewalItem: any) => (
    <div className={classes.content}>
      <div className={classes.wrapperTitle}>
        <Typography variant='h3' className={classes.title}>
          {renewalItem?.title}
        </Typography>
      </div>
      <Box marginTop={1}>
        <Typography variant='h4' component='p' className={classes.price}>
          {renewalItem?.price.toLocaleString('pt-br', { style: 'currency', currency: 'BRL' })}
        </Typography>
      </Box>
      <Box marginBottom={4}>
        <Typography variant='h6' component='p' className={classes.validity}>
          validade: {renewalItem?.validity}
        </Typography>
      </Box>
      <Button
        variant='contained'
        color='primary'
        className={classes.renewalButton}
        onClick={handleClickOpenRenewalModal}
      >
        Quero renovar!
      </Button>

      {listOffers?.renewal && listOffers?.renewal.length > 0 && (
        <RenewalModal
          renewal={listOffers?.renewal[activeSlide]}
          renewals={listOffers?.renewal}
          openRenewalModal={openRenewalModal}
          handleCloseRenewalModal={handleCloseRenewalModal}
        />
      )}
    </div>
  );

  if (!listOffers && !listOffers?.renewal) {
    return <img className={classes.image} src={bannerDefault ?? fallbackBannerLarge} alt='banner do curso' />;
  }

  return (
    <section>
      <Swiper
        id='showcase-carousel'
        preventInteractionOnTransition={true}
        allowTouchMove={false}
        slidesPerView={1}
        watchOverflow
        navigation={true}
        modules={[Navigation, Autoplay, Pagination]}
        pagination={{
          clickable: true,
        }}
        autoplay={{
          delay: 100000,
          disableOnInteraction: false,
        }}
        className={classes.slider}
        onSlideChange={({ activeIndex }) => setActiveSlide(activeIndex)}
      >
        {listOffers?.renewal.length > 0 &&
          listOffers?.renewal?.map((renewalItem) => (
            <SwiperSlide className={classes.sliderItem}>
              {isScreenLarge && (
                <div id='upsell-showcase-banner-info' className={classes.banner}>
                  {content(renewalItem)}
                </div>
              )}
              <img
                className={classes.imageBanner}
                src={previewImage(renewalItem.images.banner)}
                alt='banner do curso'
              />
            </SwiperSlide>
          ))}
      </Swiper>
      {!isScreenLarge && listOffers && (
        <div className={classes.contentInBody}>{content(listOffers?.renewal[activeSlide])}</div>
      )}
    </section>
  );
};
